import type { Module } from "@/data/adminData"

const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000"

function getTokens() {
  return {
    access: localStorage.getItem("access_token"),
    refresh: localStorage.getItem("refresh_token"),
  }
}

function setTokens(access: string, refresh: string) {
  localStorage.setItem("access_token", access)
  localStorage.setItem("refresh_token", refresh)
}

export function clearTokens() {
  localStorage.removeItem("access_token")
  localStorage.removeItem("refresh_token")
  localStorage.removeItem("user_role")
  localStorage.removeItem("tenant_id")
  localStorage.removeItem("user_id")
  localStorage.removeItem("user_email")
  localStorage.removeItem("tenant_name")
}

async function refreshAccessToken(): Promise<string | null> {
  const { refresh } = getTokens()
  if (!refresh) return null

  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  })
  if (!res.ok) {
    clearTokens()
    window.dispatchEvent(new CustomEvent("session-expired"))
    return null
  }
  const data = await res.json()
  setTokens(data.access_token, data.refresh_token)
  return data.access_token
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  retry = true,
): Promise<T> {
  const { access } = getTokens()

  const isFormData = options.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
  }
  if (access) headers["Authorization"] = `Bearer ${access}`

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers })

  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken()
    if (newToken) return apiFetch<T>(path, options, false)
    throw new ApiError(401, "Session expired")
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.detail ?? body.message ?? res.statusText, body)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
    this.name = "ApiError"
  }
}

// ── Auth ────────────────────────────────────────────────────────────────────

export async function login(email: string, password: string) {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.detail ?? "Invalid credentials")
  }
  const data: {
    access_token: string
    refresh_token: string
    role: string
    tenant_id: number | null
    user_id: number | null
    user_email: string | null
    tenant_name: string | null
  } = await res.json()
  setTokens(data.access_token, data.refresh_token)
  localStorage.setItem("user_role", data.role)
  if (data.tenant_id !== null) localStorage.setItem("tenant_id", String(data.tenant_id))
  if (data.user_id !== null) localStorage.setItem("user_id", String(data.user_id))
  if (data.user_email) localStorage.setItem("user_email", data.user_email)
  if (data.tenant_name) localStorage.setItem("tenant_name", data.tenant_name)
  return data
}

export function getStoredRole(): "admin" | "client" | null {
  return (localStorage.getItem("user_role") as "admin" | "client") ?? null
}

export function getStoredUser() {
  return {
    email: localStorage.getItem("user_email") ?? "",
    tenantName: localStorage.getItem("tenant_name") ?? "",
  }
}

// ── CLI — Jobs ───────────────────────────────────────────────────────────────

type JobStatus =
  | "done" | "partial" | "processing" | "error" | "not_configured"
  | "not_started" | "queued" | "paused" | "cancelled"

export interface JobDTO {
  id: number
  name: string
  source: "drive" | "dropbox" | "local"
  source_url: string | null
  file_count: number
  sku_count: number
  status: JobStatus
  progress: number | null
  output_format: "jpeg" | "png" | "webp" | null
  output_resolution: string | null
  naming_convention: "sku" | "sku-index" | "custom" | null
  naming_custom: string | null
  created_at: string
}

export const jobsApi = {
  list: (skip = 0, limit = 50) => apiFetch<JobDTO[]>(`/cli/jobs/?skip=${skip}&limit=${limit}`),
  get: (id: number) => apiFetch<JobDTO>(`/cli/jobs/${id}`),
  create: (body: Partial<JobDTO>) =>
    apiFetch<JobDTO>("/cli/jobs/", { method: "POST", body: JSON.stringify(body) }),
  // status/progress are server-owned; the client sends intents via `action`
  update: (id: number, body: Partial<Omit<JobDTO, "status" | "progress">> & { action?: "pause" | "cancel" | "mark_not_configured" }) =>
    apiFetch<JobDTO>(`/cli/jobs/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  start: (id: number) => apiFetch<JobDTO>(`/cli/jobs/${id}/start`, { method: "POST" }),
  remove: (id: number) => apiFetch<void>(`/cli/jobs/${id}`, { method: "DELETE" }),
  uploadSku: (jobId: number, sku: string, files: File[]) => {
    const fd = new FormData()
    fd.append("sku", sku)
    files.forEach(f => fd.append("files", f, f.name))
    return apiFetch<{ sku: string; saved: number }>(`/cli/jobs/${jobId}/upload`, { method: "POST", body: fd })
  },
  uploadFlat: (jobId: number, files: File[]) => {
    const fd = new FormData()
    fd.append("sku", "__flat__")
    files.forEach(f => fd.append("files", f, f.name))
    return apiFetch<{ sku: string; saved: number }>(`/cli/jobs/${jobId}/upload`, { method: "POST", body: fd })
  },
  startPull: (jobId: number) =>
    apiFetch<{ success: boolean; task_id: string; total: number }>(`/cli/jobs/${jobId}/pull`, { method: "POST" }),
  startAnalyze: (
    jobId: number,
    body: { imgs_per_product: number; groups?: string[]; existing_products?: unknown[] },
  ) =>
    apiFetch<{ success: boolean; task_id: string; total: number }>(`/cli/jobs/${jobId}/analyze`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getConfig: (jobId: number) => apiFetch<Record<string, unknown>>(`/cli/jobs/${jobId}/config`),
  saveConfig: (jobId: number, body: Record<string, unknown>) =>
    apiFetch<void>(`/cli/jobs/${jobId}/config`, { method: "PUT", body: JSON.stringify(body) }),

  enqueue: (jobId: number, skuIds?: string[], types?: string[]) =>
    apiFetch<{ status: string; queue_position: number; job_id: number }>(
      `/cli/jobs/${jobId}/process`,
      { method: "POST", body: JSON.stringify(skuIds ? { sku_ids: skuIds, types } : {}) },
    ),

  getProgress: (jobId: number) =>
    apiFetch<{
      job_id: number
      status: string
      skus: {
        sku: string
        reference: string
        avatar_id: string | null
        base_images: number
        done: number
        total_needed: number
        total_ops: number
        completed_ops: number
        total_generated: number
        total_images: number
        thumbnail: string | null
        output_images: string[]
        status: string
        error: string | null
        skipped: boolean
        skip_reason: string | null
        types: string[]
        type_results: Record<string, { status: string; url?: string; reason?: string; error?: string }>
      }[]
      total_done: number
      total_needed: number
      completed_ops: number
      total_ops: number
      pct: number
      failed_count: number
      skipped_count: number
      started_at: string | null
      finished_at: string | null
      duration_ms: number | null
      cost: { vision_eur: number; generation_eur: number; input_tokens: number; output_tokens: number }
    }>(`/cli/jobs/${jobId}/progress`),


  listProducts: (jobId: number, imgsPerProduct: number) =>
    apiFetch<{ group: string; marque_image: string; ref_image: string; total_images: number }[]>(`/cli/jobs/${jobId}/products?imgs_per_product=${imgsPerProduct}`),

  organize: (jobId: number, body: { organize_by: "ref" | "marque" | "marque-ref-base" | "marque-forme"; products: ProductGroupDTO[]; naming: string; naming_custom?: string; output_format?: string }) =>
    apiFetch<OrganizeResultDTO>(`/cli/jobs/${jobId}/organize`, { method: "POST", body: JSON.stringify(body) }),

  downloadOrganized: async (jobId: number, filename: string) => {
    const token = localStorage.getItem("access_token")
    const res = await fetch(`${BASE_URL}/cli/jobs/${jobId}/download-organized`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error(`Download failed: ${res.status}`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  },

  downloadCompletedZip: async (jobId: number) => {
    const token = localStorage.getItem("access_token")
    const res = await fetch(`${BASE_URL}/cli/jobs/${jobId}/download-completed`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error((body as { detail?: string }).detail ?? `Download failed: ${res.status}`)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `job-${jobId}-completed.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
}

export interface ProductGroupDTO {
  ref: string
  images: string[]
  catalogue_name: string | null
  extracted_marque: string | null
  extracted_ref: string | null
  normalized_key: string | null
  brand: string | null
  product_ref: string | null
  inventory_id: string | null
  forme: string | null
  gender: string | null
  avatar_type: string | null
  in_catalogue: boolean
}

export interface FolderNode {
  name: string
  children: FolderNode[]
  files: string[]
}
export interface OrganizeResultDTO {
  root: FolderNode
  renamed_count: number
  errors: string[]
  product_images?: Record<string, string[]>
}

// ── CLI — Settings ───────────────────────────────────────────────────────────

interface SettingsDTO {
  cloud_google_email: string | null
  cloud_dropbox_email: string | null
  custom_properties: Record<string, unknown> | null
  sku_rules: { id: number; prefix: string; avatar_id: string }[]
  avatar_product: boolean
  studio_api: boolean
  cloud_auto_transfer: boolean
  credits_budget: number
  credits_used: number
}

export const settingsApi = {
  get: () => apiFetch<SettingsDTO>("/cli/settings/"),
  update: (body: Partial<SettingsDTO>) =>
    apiFetch<SettingsDTO>("/cli/settings/", { method: "PATCH", body: JSON.stringify(body) }),
  createSkuRule: (prefix: string, avatar_id: string) =>
    apiFetch<{ id: number; prefix: string; avatar_id: string }>("/cli/settings/sku-rules", {
      method: "POST",
      body: JSON.stringify({ prefix, avatar_id }),
    }),
  deleteSkuRule: (id: number) =>
    apiFetch<void>(`/cli/settings/sku-rules/${id}`, { method: "DELETE" }),
}

// ── CLI — Cloud ───────────────────────────────────────────────────────────────

export interface CloudStatusDTO {
  google_connected: boolean
  google_email: string | null
  dropbox_connected: boolean
  dropbox_email: string | null
}

export const cloudApi = {
  status: () => apiFetch<CloudStatusDTO>("/cli/cloud/"),
  connect: (provider: "google" | "dropbox", email: string) =>
    apiFetch<CloudStatusDTO>("/cli/cloud/connect", {
      method: "POST",
      body: JSON.stringify({ provider, email }),
    }),
  disconnect: (provider: "google" | "dropbox") =>
    apiFetch<CloudStatusDTO>("/cli/cloud/disconnect", {
      method: "POST",
      body: JSON.stringify({ provider }),
    }),
  googleAuthUrl: () => apiFetch<{ url: string }>("/cli/cloud/google/auth-url"),
  dropboxAuthUrl: () => apiFetch<{ url: string }>("/cli/cloud/dropbox/auth-url"),
  startPush: (jobId: number, provider: "google" | "dropbox") =>
    apiFetch<{ success: boolean; task_id: string; total: number }>("/cli/cloud/push", {
      method: "POST",
      body: JSON.stringify({ job_id: jobId, provider }),
    }),
}

// ── Admin — Clients ───────────────────────────────────────────────────────────

export interface TenantDTO {
  id: number
  name: string
  email: string
  status: string
  storage_quota_gb: number
  storage_used_gb: number
  credits_budget: number   // float
  credits_used: number     // float
  avatar_product: boolean
  studio_api: boolean
  avatar_ids: number[]
}

export const adminClientsApi = {
  list: (skip = 0, limit = 50) => apiFetch<TenantDTO[]>(`/admin/clients/?skip=${skip}&limit=${limit}`),
  create: (body: Partial<TenantDTO> & { password: string }) =>
    apiFetch<TenantDTO>("/admin/clients/", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<TenantDTO>) =>
    apiFetch<TenantDTO>(`/admin/clients/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<void>(`/admin/clients/${id}`, { method: "DELETE" }),
}

// ── Admin — Avatars ───────────────────────────────────────────────────────────

export interface AvatarFolderDTO { id: number; name: string; parent_id: number | null; is_private: boolean }
export interface AvatarAssetDTO { id: number; name: string; img_path: string; folder_id: number | null; is_private: boolean }

export const adminAvatarsApi = {
  listFolders: () => apiFetch<AvatarFolderDTO[]>("/admin/avatars/folders"),
  createFolder: (body: Partial<AvatarFolderDTO>) =>
    apiFetch<AvatarFolderDTO>("/admin/avatars/folders", { method: "POST", body: JSON.stringify(body) }),
  deleteFolder: (id: number) => apiFetch<void>(`/admin/avatars/folders/${id}`, { method: "DELETE" }),
  listAssets: () => apiFetch<AvatarAssetDTO[]>("/admin/avatars/assets"),
  createAsset: (body: Partial<AvatarAssetDTO>) =>
    apiFetch<AvatarAssetDTO>("/admin/avatars/assets", { method: "POST", body: JSON.stringify(body) }),
  updateAsset: (id: number, body: Partial<AvatarAssetDTO>) =>
    apiFetch<AvatarAssetDTO>(`/admin/avatars/assets/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteAsset: (id: number) => apiFetch<void>(`/admin/avatars/assets/${id}`, { method: "DELETE" }),
  uploadAsset: (file: File, folderId: number | null) => {
    const fd = new FormData()
    fd.append("file", file)
    if (folderId !== null) fd.append("folder_id", String(folderId))
    return apiFetch<AvatarAssetDTO>("/admin/avatars/upload", { method: "POST", body: fd })
  },
  updateFolder: (id: number, body: Partial<AvatarFolderDTO>) =>
    apiFetch<AvatarFolderDTO>(`/admin/avatars/folders/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  grantToTenant: (tenantId: number, assetId: number) =>
    apiFetch<void>(`/admin/avatars/tenants/${tenantId}/assets/${assetId}`, { method: "POST" }),
  revokeFromTenant: (tenantId: number, assetId: number) =>
    apiFetch<void>(`/admin/avatars/tenants/${tenantId}/assets/${assetId}`, { method: "DELETE" }),
}

// ── Admin — API Keys ──────────────────────────────────────────────────────────

export interface ApiKeyDTO { id: string; name: string; usage: string; model_vision: string | null; model_generation: string | null; used_tokens: number; total_tokens: number; is_active: boolean; key_hint?: string | null }
interface ApiKeyTestResult { ok: boolean; message: string }

export const adminApiKeysApi = {
  list: () => apiFetch<ApiKeyDTO[]>("/admin/api-keys/"),
  test: (body: { provider: string; key: string }) =>
    apiFetch<ApiKeyTestResult>("/admin/api-keys/test", { method: "POST", body: JSON.stringify(body) }),
  create: (body: { id: string; name: string; key: string; usage?: string; model_vision?: string | null; model_generation?: string | null; total_tokens?: number }) =>
    apiFetch<ApiKeyDTO>("/admin/api-keys/", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: { name?: string; key?: string; usage?: string; model_vision?: string | null; model_generation?: string | null; total_tokens?: number; is_active?: boolean }) =>
    apiFetch<ApiKeyDTO>(`/admin/api-keys/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => apiFetch<void>(`/admin/api-keys/${id}`, { method: "DELETE" }),
}

// ── Admin — VPS ───────────────────────────────────────────────────────────────

export interface VpsStatsDTO {
  cpu_percent: number
  disk: { total_gb: number; used_gb: number; free_gb: number; percent: number }
  disk_breakdown: {
    system_gb: number
    clients_gb: number
    assets_gb: number
    temp_gb: number
    free_gb: number
  }
  client_storage: Array<{
    tenant_id: number
    name: string
    used_gb: number
    quota_gb: number
  }>
  memory: { total_gb: number; used_gb: number; percent: number }
  uptime_seconds: number
}

export const adminVpsApi = {
  stats: () => apiFetch<VpsStatsDTO>("/admin/vps/"),
}

// ── Admin — System ────────────────────────────────────────────────────────────

interface UsageRowDTO {
  tenant_id: number
  kind: "vision" | "generation"
  cost_eur: number
  input_tokens: number
  output_tokens: number
  events: number
}

// { "<provider>:<model>": { input_eur_per_mtok, output_eur_per_mtok }, batch_discount: 0.5 }
export type AiPricesDTO = Record<string, { input_eur_per_mtok?: number; output_eur_per_mtok?: number } | number>

export const adminSystemApi = {
  getMaintenance: () => apiFetch<{ enabled: boolean }>("/admin/system/maintenance"),
  setMaintenance: (enabled: boolean) =>
    apiFetch<{ enabled: boolean }>("/admin/system/maintenance", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),
  getPrices: () => apiFetch<AiPricesDTO>("/admin/system/prices"),
  setPrices: (body: AiPricesDTO) =>
    apiFetch<AiPricesDTO>("/admin/system/prices", { method: "PUT", body: JSON.stringify(body) }),
  getUsage: (tenantId?: number) =>
    apiFetch<UsageRowDTO[]>(`/admin/system/usage${tenantId != null ? `?tenant_id=${tenantId}` : ""}`),
}

// Public (no auth) — safe to call from CLI context
export async function fetchMaintenanceStatus(): Promise<boolean> {
  const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000"
  try {
    const res = await fetch(`${BASE_URL}/system/maintenance`)
    if (!res.ok) return false
    const data = await res.json()
    return data.enabled === true
  } catch {
    return false
  }
}

// ── Admin — Users ─────────────────────────────────────────────────────────────

export interface AdminUserDTO { id: number; email: string; role: string }

export const adminUsersApi = {
  list: () => apiFetch<AdminUserDTO[]>("/admin/users/"),
  create: (body: { email: string; password: string }) =>
    apiFetch<AdminUserDTO>("/admin/users/", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: { email?: string; password?: string }) =>
    apiFetch<AdminUserDTO>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" }),
}

// ── CLI — API Keys ────────────────────────────────────────────────────────────

interface TenantApiKeyDTO {
  id: number
  provider: string
  label: string
  usage: string
  model_vision: string | null
  model_generation: string | null
  vertex_location: string | null
  is_active: boolean
  key_hint: string
}

export const cliApiKeysApi = {
  list: () => apiFetch<TenantApiKeyDTO[]>("/cli/api-keys/"),
  test: (body: { provider: string; key: string }) =>
    apiFetch<ApiKeyTestResult>("/cli/api-keys/test", { method: "POST", body: JSON.stringify(body) }),
  create: (body: { provider: string; label: string; key: string; usage?: string; model_vision?: string | null; model_generation?: string | null; vertex_location?: string | null }) =>
    apiFetch<TenantApiKeyDTO>("/cli/api-keys/", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: { label?: string; key?: string; usage?: string; model_vision?: string | null; model_generation?: string | null; vertex_location?: string | null; is_active?: boolean }) =>
    apiFetch<TenantApiKeyDTO>(`/cli/api-keys/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<void>(`/cli/api-keys/${id}`, { method: "DELETE" }),
}

// ── CLI — Storage ─────────────────────────────────────────────────────────────

export interface StorageDTO {
  used_gb: number
  quota_gb: number
  free_gb: number
  percent: number
  system_reserved_gb: number
  images_gb: number
  image_count: number
  generated_gb: number
  generated_count: number
}

export const storageApi = {
  get: () => apiFetch<StorageDTO>("/cli/storage/"),
}

// ── CLI — Catalogue ───────────────────────────────────────────────────────────

export interface CsvSchemaDTO {
  mapping: Record<string, string> | null
  separator: string | null
}

export interface CatalogueDTO {
  id: number
  name: string
  source_file: string
  product_count: number
  image_count: number
  imported_at: string
  has_duplicates: boolean
}

export interface CatalogueSearchResultDTO extends CatalogueProductDTO {
  catalogue_id: number
  catalogue_name: string
}

export interface CatalogueProductDTO {
  id: number
  ref_key: string
  brand: string | null
  product_ref: string | null
  inventory_id: string | null
  gender: string | null
  forme: string | null
  avatar_type: string | null  // "Optique" | "Optique + Solaire" | "Solaire"
  images_uploaded: boolean
  status: "not_started" | "processing" | "done" | "error"
  error_message: string | null
  is_duplicate: boolean
  link_group_id: string | null
  linked_count: number
}

interface DiffItemDTO {
  ref_key: string
  change: "added" | "removed" | "modified"
  old_data: Record<string, string> | null
  new_data: Record<string, string> | null
}

export interface DiffResultDTO {
  catalogue_id: number
  added: DiffItemDTO[]
  removed: DiffItemDTO[]
  modified: DiffItemDTO[]
  unchanged: number
}

export const catalogueApi = {
  getSchema: () => apiFetch<CsvSchemaDTO>("/cli/catalogue/schema"),
  saveSchema: (mapping: Record<string, string>, separator: string) =>
    apiFetch<CsvSchemaDTO>("/cli/catalogue/schema", { method: "PUT", body: JSON.stringify({ mapping, separator }) }),

  list: () => apiFetch<CatalogueDTO[]>("/cli/catalogue/"),
  import: (file: File) => {
    const fd = new FormData()
    fd.append("file", file)
    return apiFetch<CatalogueDTO>("/cli/catalogue/import", { method: "POST", body: fd })
  },
  products: (catalogueId: number) => apiFetch<CatalogueProductDTO[]>(`/cli/catalogue/${catalogueId}/products`),
  delete: (catalogueId: number) => apiFetch<void>(`/cli/catalogue/${catalogueId}`, { method: "DELETE" }),

  diff: (catalogueId: number, file: File) => {
    const fd = new FormData()
    fd.append("file", file)
    return apiFetch<DiffResultDTO>(`/cli/catalogue/${catalogueId}/diff`, { method: "POST", body: fd })
  },
  applyDiff: (catalogueId: number, file: File, apply: string[]) => {
    const fd = new FormData()
    fd.append("file", file)
    fd.append("body", JSON.stringify({ catalogue_id: catalogueId, apply }))
    return apiFetch<{ status: string }>(`/cli/catalogue/${catalogueId}/apply-diff`, { method: "POST", body: fd })
  },

  exportUrl: (catalogueId: number) =>
    `${import.meta.env.VITE_API_URL ?? "http://localhost:8000"}/cli/catalogue/${catalogueId}/export`,

  autoLink: () => apiFetch<{ groups_created: number; products_linked: number }>("/cli/catalogue/auto-link", { method: "POST" }),
  backfillForme: () => apiFetch<{ updated: number; total: number }>("/cli/catalogue/backfill-forme", { method: "POST" }),
  patchProduct: (id: number, body: { forme?: string | null; gender?: string | null; avatar_type?: string | null }) =>
    apiFetch<CatalogueProductDTO>(`/cli/catalogue/products/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  search: (q: string) => apiFetch<CatalogueSearchResultDTO[]>(`/cli/catalogue/search?q=${encodeURIComponent(q)}`),
}

// ── Admin — Modules ───────────────────────────────────────────────────────────

export interface ModulePromptDTO {
  id: number
  module_id: string
  version: number
  label: string
  content: string
  is_active: boolean
  created_at: string
}

export interface ActivityEventDTO {
  id: number
  tenant_name: string
  job_name: string
  status: string
  created_at: string
}

export const adminActivityApi = {
  list: (limit = 20) => apiFetch<ActivityEventDTO[]>(`/admin/activity/?limit=${limit}`),
}

export interface EnabledModuleDTO { module_id: string; name: string; description: string; category: string; enabled: boolean }

export const cliModulesApi = {
  list: () => apiFetch<EnabledModuleDTO[]>("/cli/modules/"),
  prompts: (moduleId: string) => apiFetch<ModulePromptDTO[]>(`/cli/modules/${moduleId}/prompts`),
}

interface CliAvatarDTO { id: number; name: string; img_path: string; folder_id: number | null; folder_name: string | null }

export const cliAvatarsApi = {
  list: () => apiFetch<CliAvatarDTO[]>("/cli/settings/avatars"),
}

export interface TenantModuleDTO { module_id: string; name: string; description: string; category: string; enabled: boolean }

export const adminTenantModulesApi = {
  list:   (tenantId: number)                          => apiFetch<TenantModuleDTO[]>(`/admin/tenants/${tenantId}/modules`),
  toggle: (tenantId: number, moduleId: string, enabled: boolean) =>
    apiFetch<TenantModuleDTO>(`/admin/tenants/${tenantId}/modules/${moduleId}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
}

export const adminModulesApi = {
  list:   ()                                   => apiFetch<Module[]>("/admin/modules/"),
  reload: ()                                   => apiFetch<Module[]>("/admin/modules/reload", { method: "POST" }),
  update: (id: string, data: Partial<Module>) => apiFetch<Module>(`/admin/modules/${id}`,    { method: "PATCH", body: JSON.stringify(data) }),
  remove: (id: string)                         => apiFetch<void>(`/admin/modules/${id}`,      { method: "DELETE" }),

  prompts: {
    list:     (moduleId: string)                                          => apiFetch<ModulePromptDTO[]>(`/admin/modules/${moduleId}/prompts`),
    create:   (moduleId: string, body: { content: string; label: string }) => apiFetch<ModulePromptDTO>(`/admin/modules/${moduleId}/prompts`,                   { method: "POST",   body: JSON.stringify(body) }),
    activate: (moduleId: string, promptId: number)                        => apiFetch<ModulePromptDTO[]>(`/admin/modules/${moduleId}/prompts/${promptId}/activate`, { method: "POST" }),
    remove:   (moduleId: string, promptId: number)                        => apiFetch<void>(`/admin/modules/${moduleId}/prompts/${promptId}`,                    { method: "DELETE" }),
  },
}

export interface ActivityLogDTO {
  id: number
  tenant_id: number | null
  tenant_name: string | null
  user_id: number | null
  action: string
  metadata: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

export interface DebugStatsDTO {
  today: {
    jobs_completed: number
    total_generations: number
    errors: number
    avg_duration_ms: number
    models_used: Record<string, number>
  }
  all_time: { jobs_completed: number }
}

export interface DebugQueueDTO {
  queue_depth: number
  active_count: number
  queued: { job_id: number; job_name: string; tenant_id: number; tenant_name: string }[]
}

export interface DebugLiveDTO {
  active_jobs: {
    worker_id: number
    job_id: number
    tenant_id: number
    sku: string | null
    type_: string | null
    done: number
    total: number
    elapsed_s: number
    model?: string
    provider?: string
  }[]
}

// ── CLI — Tasks (fire-and-poll) ───────────────────────────────────────────────

export interface TaskStatus {
  task_id: string
  task_type: string
  job_id: number
  status: "queued" | "running" | "done" | "error"
  done: number
  total: number
  error: string | null
  reload_required: boolean
  reload_reason: string | null
  reload_targets: string[]
  result: Record<string, unknown> | null
  partial_products?: unknown[]
}

export const tasksApi = {
  getStatus: async (taskId: string): Promise<TaskStatus | { status: "not_found" }> => {
    try {
      return await apiFetch<TaskStatus>(`/cli/tasks/${taskId}/status`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return { status: "not_found" }
      throw e
    }
  },
}

export const debugApi = {
  live:     ()                                           => apiFetch<DebugLiveDTO>("/admin/debug/live"),
  queue:    ()                                           => apiFetch<DebugQueueDTO>("/admin/debug/queue"),
  stats:    ()                                           => apiFetch<DebugStatsDTO>("/admin/debug/stats"),
  activity: (params?: { tenant_id?: number; action?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams()
    if (params?.tenant_id != null) q.set("tenant_id", String(params.tenant_id))
    if (params?.action)  q.set("action", params.action)
    if (params?.limit)   q.set("limit", String(params.limit))
    if (params?.offset)  q.set("offset", String(params.offset))
    return apiFetch<ActivityLogDTO[]>(`/admin/debug/activity?${q}`)
  },
}
